import React, { useState, useEffect } from 'react';
import { FileText, Calendar, DollarSign } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl } from "../../config/api";
import { useSubscription } from '../../hooks/useSubscription';

export default function RecentEntriesWidget({ config = {} }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { hasFeature } = useSubscription();

  const { 
    title = 'Recent Entries',
    maxEntries = 5
  } = config;

  useEffect(() => {
    fetchRecentEntries();
  }, []);

  const fetchRecentEntries = async () => {
    try {
      setLoading(true);
      const response = await axios.get(getApiUrl('/api/entries'));

      if (response.data) {
        // Take the most recent entries
        const recentEntries = response.data.slice(0, maxEntries);
        setEntries(recentEntries);
      }
    } catch (error) {
      console.error('Error fetching recent entries:', error);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const getEntryType = (entry) => {
    // Check if entry is from KCIRodeo
    if (entry.entry_data?.source === 'kcirodeo') {
      return 'imported';
    }
    // Otherwise it's a manual entry
    return 'manual';
  };

  const getEntryTypeColor = (entryType) => {
    switch (entryType) {
      case 'imported':
        return 'text-blue-600 bg-blue-100';
      case 'manual':
        return 'text-green-600 bg-green-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const handleEntryClick = () => {
    navigate('/entries');
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-3">
          <FileText className="h-5 w-5 text-green-600" />
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
        <div className="animate-pulse space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-4 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-3">
          <FileText className="h-5 w-5 text-green-600" />
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
        <p className="text-red-600 text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex items-center gap-2 mb-3">
        <FileText className="h-5 w-5 text-green-600" />
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      </div>

      {entries.length === 0 ? (
        <p className="text-gray-500 text-sm">No recent entries</p>
      ) : (
        <div className="space-y-3">
          {entries.map((entry) => {
            const entryType = getEntryType(entry);
            return (
              <div 
                key={entry.id} 
                className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={handleEntryClick}
              >
                <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <FileText className="h-5 w-5 text-green-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-gray-900 truncate">
                      {entry.show_name || 'Untitled Show'}
                    </h4>
                    <span className={`text-xs px-2 py-1 rounded-full ${getEntryTypeColor(entryType)}`}>
                      {entryType}
                    </span>
                  </div>
                  {hasFeature('family_plan') && entry.owner_name && (
                    <div className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded mt-1 inline-block">{entry.owner_name}</div>
                  )}
                  <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {entry.show_start_date ? format(parseISO(entry.show_start_date), 'MMM d, yyyy') : 'No date'}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
} 